import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Select, MenuItem, IconButton, Typography } from '@material-ui/core';
import {
    FirstPageRounded as FirstPageIcon,
    LastPageRounded as LastPageIcon,
    NavigateBeforeRounded as PrevIcon,
    NavigateNextRounded as NextIcon,
    ArrowDropDownRounded as ArrowDropDownIcon,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

const PaginationWrapper = styled.div`
  width: inherit;
  display: flex;
  align-self: flex-end;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 16px;
  gap: 8px;
  border-top: 1px solid #e0e3e9;
`;

const StyledSelect = styled(Select)`
  margin-right: 16px;
  min-width: 100px;

  .MuiSelect-select {
    padding: 8px 32px 8px 8px;
    background: #ffffff !important;
    border: 1px solid #e0e3e9;
    border-radius: 4px;

    &:focus {
      border-radius: 4px;
      background: #ffffff !important;
    }
  }

  // Override Material-UI's default focused styles
  .MuiInput-underline:after,
  .MuiInput-underline:before {
    display: none;
  }

  // Style for the dropdown icon
  .MuiSelect-icon {
    right: 8px;
  }

  // Ensure background stays white in all states
  &.Mui-focused .MuiSelect-select {
    background: #ffffff !important;
    border-color: #e0e3e9;
  }

  // Style for hover state
  &:hover .MuiSelect-select {
    border-color: #b0b5be;
  }

  // Ensure the border and background persist in all states
  .MuiSelect-select.MuiSelect-select {
    background: #ffffff !important;
    border: 1px solid #e0e3e9;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  padding: 8px 16px;
  min-height: 40px;

  &:hover {
    background-color: #f5f6f8;
  }

  &.Mui-selected {
    background-color: #f5f6f8;

    &:hover {
      background-color: #e8e9ec;
    }
  }
`;

const PageInfo = styled(Typography)`
  margin: 0 16px;
  min-width: 100px;
  text-align: center;
`;

const NavigationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledIconButton = styled(IconButton)`
  ${props => props.$isHidden && `
    visibility: hidden;
  `}
`;

export const DEFAULT_ROWS_PER_PAGE = 10;
export const DEFAULT_ROWS_OPTIONS = [10, 20, 50];

const Pagination = ({
    itemCount = 0,
    currentPage = 0,
    rowsPerPage = DEFAULT_ROWS_PER_PAGE,
    rowsPerPageOptions = DEFAULT_ROWS_OPTIONS,
    handleChangeRowsPerPage,
    handleChangePage,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    hidePagination = false,
}) => {
    const { t } = useTranslation();
    const maxPages = Math.ceil(itemCount / rowsPerPage) || 1;
    const isFirstPage = currentPage === 0;
    const isLastPage = currentPage === maxPages - 1;

    // Default handlers if not provided
    const handleNext = (e) => {
        if (!isLastPage) {
            handleChangePage?.(e, currentPage + 1);
            handleNextPage?.();
        }
    };

    const handlePrev = (e) => {
        if (!isFirstPage) {
            handleChangePage?.(e, currentPage - 1);
            handlePrevPage?.();
        }
    };

    const handleFirst = (e) => {
        if (!isFirstPage) {
            handleChangePage?.(e, 0);
            handleFirstPage?.();
        }
    };

    const handleLast = (e) => {
        if (!isLastPage) {
            handleChangePage?.(e, maxPages - 1);
            handleLastPage?.();
        }
    };

    const handleRowsChange = (event) => {
        handleChangeRowsPerPage?.(event);
    };

    if (hidePagination || itemCount === 0) {
        return null;
    }

    return (
        <PaginationWrapper>
            <StyledSelect
                value={rowsPerPage}
                onChange={handleRowsChange}
                disableUnderline
                variant="standard"
                renderValue={value => `${value} ${t('rows')}`}
                IconComponent={ArrowDropDownIcon}
            >
                {rowsPerPageOptions.map(option => (
                    <StyledMenuItem key={option} value={option}>
                        {`${option} ${t('rows')}`}
                    </StyledMenuItem>
                ))}
            </StyledSelect>

            <NavigationButtons>
                <StyledIconButton
                    size="small"
                    onClick={handleFirst}
                    $isHidden={isFirstPage}
                >
                    <FirstPageIcon />
                </StyledIconButton>
                <StyledIconButton
                    size="small"
                    onClick={handlePrev}
                    $isHidden={isFirstPage}
                >
                    <PrevIcon />
                </StyledIconButton>

                <PageInfo variant="body2">
                    {t('page')} {currentPage + 1} {t('of')} {maxPages}
                </PageInfo>

                <StyledIconButton
                    size="small"
                    onClick={handleNext}
                    $isHidden={isLastPage}
                >
                    <NextIcon />
                </StyledIconButton>
                <StyledIconButton
                    size="small"
                    onClick={handleLast}
                    $isHidden={isLastPage}
                >
                    <LastPageIcon />
                </StyledIconButton>
            </NavigationButtons>
        </PaginationWrapper>
    );
};

Pagination.propTypes = {
    itemCount: PropTypes.number,
    currentPage: PropTypes.number,
    rowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    handleChangeRowsPerPage: PropTypes.func,
    handleChangePage: PropTypes.func,
    handleNextPage: PropTypes.func,
    handlePrevPage: PropTypes.func,
    handleFirstPage: PropTypes.func,
    handleLastPage: PropTypes.func,
    hidePagination: PropTypes.bool
};

export default Pagination;